"use client"

import Link from "next/link"
import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, ChevronRight, X } from "lucide-react"
import type { Article } from "@/lib/types"

interface WebStoriesListProps {
  articles: Article[]
}

function getExcerpt(content: string) {
  const text = content.replace(/<[^>]*>/g, "").trim()
  return text.length > 70 ? text.substring(0, 70) + "..." : text
}

export function WebStoriesList({ articles }: WebStoriesListProps) {
  const listRef = useRef<HTMLDivElement | null>(null)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const activeArticle = useMemo(
    () => (activeIndex === null ? null : articles[activeIndex] ?? null),
    [activeIndex, articles],
  )

  const scrollByAmount = (direction: "left" | "right") => {
    if (!listRef.current) return
    const amount = 280
    listRef.current.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    })
  }

  useEffect(() => {
    if (activeIndex === null) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveIndex(null)
      if (e.key === "ArrowLeft") {
        setActiveIndex((prev) => {
          if (prev === null) return prev
          return (prev - 1 + articles.length) % articles.length
        })
      }
      if (e.key === "ArrowRight") {
        setActiveIndex((prev) => {
          if (prev === null) return prev
          return (prev + 1) % articles.length
        })
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [activeIndex, articles.length])

  const openStory = (index: number) => setActiveIndex(index)
  const closeStory = () => setActiveIndex(null)
  const showPrev = () =>
    setActiveIndex((prev) => (prev === null ? prev : (prev - 1 + articles.length) % articles.length))
  const showNext = () =>
    setActiveIndex((prev) => (prev === null ? prev : (prev + 1) % articles.length))

  if (!articles?.length) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        <p>No web stories available</p>
      </div>
    )
  }

  return (
    <div
      className="relative rounded-md p-3"
      style={{
        border: "1px solid #dbe3f5",
        background: "#f6f9ff",
      }}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <h3 className="text-base font-semibold text-[#e40046] whitespace-nowrap">வெப்ஸ்டோரிஸ்</h3>
          <div
            className="h-[3px] rounded-full flex-1"
            style={{
              background: "linear-gradient(90deg, #f04f8a 0%, #f89b3c 50%, #4a86ff 100%)",
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollByAmount("left")}
            className="h-7 w-7 rounded-full border bg-white text-gray-700 hover:bg-gray-100 inline-flex items-center justify-center"
            aria-label="Scroll web stories left"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollByAmount("right")}
            className="h-7 w-7 rounded-full border bg-white text-gray-700 hover:bg-gray-100 inline-flex items-center justify-center"
            aria-label="Scroll web stories right"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div
        ref={listRef}
        className="flex gap-3 overflow-x-auto scroll-smooth pb-1"
        style={{ scrollbarWidth: "thin" }}
      >
        {articles.map((article, index) => (
          <button
            key={article.id}
            type="button"
            onClick={() => openStory(index)}
            className="relative block min-w-[220px] w-[220px] h-[290px] rounded-md overflow-hidden group flex-shrink-0"
            style={{
              boxShadow: "1px 1px 5px rgba(0, 0, 0, .18)",
            }}
          >
            {article.featured_image ? (
              <img
                src={article.featured_image}
                alt={article.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center">
                <span className="text-muted-foreground text-xs">No Image</span>
              </div>
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />

            <div className="absolute top-2 left-2">
              <span className="text-[10px] font-semibold tracking-wide px-2 py-1 rounded text-white bg-[#ff4d4f]">
                SHORT STORY
              </span>
            </div>

            <div className="absolute left-0 right-0 bottom-0 p-3 text-white">
              <h4 className="text-sm font-semibold leading-5 line-clamp-3 mb-1">
                {article.title}
              </h4>
              <p className="text-xs text-white/85 line-clamp-2">
                {getExcerpt(article.content)}
              </p>
            </div>
          </button>
        ))}
      </div>

      {activeArticle && (
        <div className="fixed inset-0 z-[100]">
          <div className="absolute inset-0 bg-black/75" onClick={closeStory} />

          <button
            type="button"
            onClick={showPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-[101] h-10 w-10 rounded-full bg-white text-black shadow inline-flex items-center justify-center hover:bg-gray-100"
            aria-label="Previous story"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>

          <button
            type="button"
            onClick={showNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-[101] h-10 w-10 rounded-full bg-white text-black shadow inline-flex items-center justify-center hover:bg-gray-100"
            aria-label="Next story"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="absolute left-1/2 top-1/2 z-[101] w-[92vw] max-w-[380px] -translate-x-1/2 -translate-y-1/2">
            <div className="overflow-hidden rounded-md bg-white shadow-2xl">
              <div className="relative h-[220px] bg-black">
                {activeArticle.featured_image ? (
                  <img
                    src={activeArticle.featured_image}
                    alt={activeArticle.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/70 text-sm">
                    No Image
                  </div>
                )}
                <button
                  type="button"
                  onClick={closeStory}
                  className="absolute right-2 top-2 h-7 w-7 rounded-full bg-black/60 text-white inline-flex items-center justify-center hover:bg-black/75"
                  aria-label="Close story popup"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="max-h-[52vh] overflow-y-auto p-3 text-left">
                <h4 className="text-base font-semibold leading-6 text-black mb-2">
                  {activeArticle.title}
                </h4>
                <p className="text-sm text-gray-700 leading-6 whitespace-pre-wrap">
                  {activeArticle.content.replace(/<[^>]*>/g, "").trim()}
                </p>
                <div className="mt-3">
                  <Link
                    href={`/article/${activeArticle.slug}`}
                    className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-black"
                  >
                    Read more
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

